import { usePage } from '@inertiajs/react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip
} from '@mui/material';
import {
    Timeline as TimelineIcon,
    Notifications as NotificationsIcon,
    TouchApp as TouchAppIcon,
    TrendingUp as TrendingUpIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';

interface TimelineDataPoint {
    date: string;
    system_events: number;
    feature_usages: number;
    total: number;
}

interface Event {
    id: number;
    type: 'system_notification' | 'feature_usage';
    event_type: string;
    message: string;
    user_name?: string;
    user_email?: string;
    user_type?: string;
    status?: string;
    created_at: string;
}

interface Stats {
    total_system_events: number;
    total_feature_usages: number;
    events_today: number;
    total_events: number;
}

interface Props {
    events: Event[];
    timeline_data: TimelineDataPoint[];
    stats: Stats;
    [key: string]: any;
}

export default function SystemLogsPage() {
    const { props } = usePage<Props>();
    const { events, timeline_data, stats } = props;

    const getEventColor = (type: string) => {
        if (type === 'system_notification') return 'primary';
        if (type === 'feature_usage') return 'success';
        return 'default';
    };

    const getEventIcon = (type: string) => {
        if (type === 'system_notification') return <NotificationsIcon fontSize="small" />;
        if (type === 'feature_usage') return <TouchAppIcon fontSize="small" />;
        return <NotificationsIcon fontSize="small" />;
    };

    const formatDate = (dateString: string) => {
        try {
            return format(parseISO(dateString), 'MMM dd, yyyy h:mm a');
        } catch {
            return dateString;
        }
    };

    const formatChartDate = (dateString: string) => {
        try {
            return format(parseISO(dateString), 'MMM dd');
        } catch {
            return dateString;
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <TimelineIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    System Logs
                </Typography>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <NotificationsIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {stats.total_system_events}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        System Events
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TouchAppIcon sx={{ fontSize: 32, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {stats.total_feature_usages}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Feature Usages
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarIcon sx={{ fontSize: 32, color: 'info.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {stats.events_today}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Events Today
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUpIcon sx={{ fontSize: 32, color: 'warning.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {stats.total_events}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Events
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Timeline Chart */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Activity Timeline (Last 30 Days)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={timeline_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatChartDate}
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis style={{ fontSize: '12px' }} />
                            <RechartsTooltip
                                labelFormatter={formatChartDate}
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px'
                                }}
                            />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="system_events"
                                stroke="#1976d2"
                                name="System Events"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="feature_usages"
                                stroke="#2e7d32"
                                name="Feature Usages"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="total"
                                stroke="#ed6c02"
                                name="Total Events"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>

            {/* Event Log Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Recent Events (Last 50)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date/Time</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => (
                                    <TableRow
                                        key={`${event.type}-${event.id}`}
                                        hover
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>
                                            <Chip
                                                icon={getEventIcon(event.type)}
                                                label={event.type === 'system_notification' ? 'System' : 'Feature'}
                                                color={getEventColor(event.type)}
                                                size="small"
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {event.event_type}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={event.message} arrow>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 300,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {event.message}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {event.user_name ? (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {event.user_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {event.user_type}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    System
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(event.created_at)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {events.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No events found
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
}

